// contract-manager/client/pages/access-control/exceptions/index.jsx
import { useMemo, useState } from 'react'
import { useLoaderData, useSearchParams, Link, useRevalidator } from 'react-router-dom'
import axios from 'axios'
import {
  Button, Chip, Typography,
  Tooltip, IconButton, Dialog, DialogHeader, DialogBody, DialogFooter, Textarea,
  Menu, MenuHandler, MenuList, MenuItem
} from '@material-tailwind/react'
import { HasAccess } from '@permify/react-role'
import { FiX, FiChevronDown, FiCornerUpLeft, FiClock, FiPlus } from 'react-icons/fi'
import PropTypes from 'prop-types'
import { DateRangeCell } from '@/shared/components/date-range-cell'

const JUSTIFICATION_WORD_LIMIT = 250
const STATUS_COLORS = {
  active: 'green',
  pending: 'amber',
  expired: 'blue-gray',
  used: 'blue',
  canceled: 'red'
}
const STATUS_LABELS = {
  active: 'Activa',
  pending: 'En espera',
  expired: 'Expirada',
  used: 'Usada',
  canceled: 'Cancelada'
}

const normalizeException = (r) => ({
  id: r.accountScopedId,
  subject_type: r.subject_type ?? r.subjectType,
  subject_id: r.subject_id ?? r.subjectId,
  subject_name: r.subject_name ?? r.subjectName,
  subject_subtitle: r.subject_subtitle ?? r.subjectSubtitle,
  office_id: r.office_id ?? r.officeId,
  office_name: r.office_name ?? r.officeName,
  event_scope: r.event_scope ?? r.eventScope,
  kind: r.kind,
  justification: r.justification,
  granted_by_id: r.granted_by_id ?? r.grantedById,
  granted_by_name: r.granted_by_name ?? r.grantedByName,
  starts_at: r.starts_at ?? r.startsAt,
  ends_at: r.ends_at ?? r.endsAt,
  status: r.status
})

const buildJustificationPreview = (justification) => {
  const text = justification ? String(justification) : ''
  const words = text.trim().match(/\S+/g) || []

  if (words.length <= JUSTIFICATION_WORD_LIMIT) {
    return { preview: text, truncated: false }
  }

  return {
    preview: words.slice(0, JUSTIFICATION_WORD_LIMIT).join(' '),
    truncated: true
  }
}

export async function ExceptionsListLoader ({ request }) {
  const url = new URL(request.url)
  const sp = url.searchParams
  if (!sp.get('page')) sp.set('page', '1')
  if (!sp.get('per_page')) sp.set('per_page', '10')
  const qs = sp.toString()
  const { data } = await axios.get(`/api/v1/access_control_exceptions?${qs}`)
  return data
}

function StatusPill ({ value }) {
  const color = STATUS_COLORS[value] || 'gray'
  const label = STATUS_LABELS[value] || value
  return <Chip value={label} color={color} size="sm" className="capitalize" />
}

StatusPill.propTypes = {
  value: PropTypes.string
}

function Pagination ({ meta, searchParams, setSearchParams }) {
  if (!meta) return null
  const { page, pages } = meta
  const prev = () => {
    if (page <= 1) return
    const sp = new URLSearchParams(searchParams.toString())
    sp.set('page', String(page - 1))
    setSearchParams(sp)
  }
  const next = () => {
    if (pages && page >= pages) return
    const sp = new URLSearchParams(searchParams.toString())
    sp.set('page', String(page + 1))
    setSearchParams(sp)
  }
  return (
    <div className="flex items-center justify-between mt-4">
      <Typography variant="small">Página {page} / {pages || '?'}</Typography>
      <div className="flex gap-2">
        <Button onClick={prev} variant="outlined" disabled={page <= 1}>Anterior</Button>
        <Button onClick={next} disabled={pages ? page >= pages : false}>Siguiente</Button>
      </div>
    </div>
  )
}

Pagination.propTypes = {
  meta: PropTypes.shape({
    page: PropTypes.number,
    pages: PropTypes.number
  }),
  searchParams: PropTypes.object.isRequired,
  setSearchParams: PropTypes.func.isRequired
}

function JustificationCell ({ justification, onShowFull }) {
  const { preview, truncated } = buildJustificationPreview(justification)

  if (!preview.trim()) {
    return <td className="p-3 align-top w-[340px] max-w-[340px]">-</td>
  }

  return (
    <td className="p-3 align-top w-[340px] max-w-[340px]">
      <div className="max-w-[340px] whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm leading-5 text-blue-gray-800">
        {preview}
        {truncated && (
          <>
            {' '}
            <button
              type="button"
              className="font-medium text-blue-700 underline decoration-blue-700 underline-offset-2 hover:text-blue-900"
              onClick={() => onShowFull(String(justification))}
            >
              Ver más...
            </button>
          </>
        )}
      </div>
    </td>
  )
}

JustificationCell.propTypes = {
  justification: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  onShowFull: PropTypes.func.isRequired
}

export default function AccessControlExceptionsPage () {
  const data = useLoaderData()
  const [searchParams, setSearchParams] = useSearchParams()
  const revalidator = useRevalidator()

  const { records, meta } = useMemo(() => {
    if (!data) return { records: [], meta: null }
    if (Array.isArray(data)) return { records: data, meta: null }
    if (Array.isArray(data?.data)) return { records: data.data, meta: data.meta || null }
    const arrayKey = Object.keys(data).find(k => Array.isArray(data[k]))
    return { records: arrayKey ? data[arrayKey] : [], meta: data.meta || null }
  }, [data])

  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelId, setCancelId] = useState(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelError, setCancelError] = useState('')
  const [fullJustification, setFullJustification] = useState('')

  const closeJustificationModal = () => setFullJustification('')

  const openCancel = (id) => {
    setCancelId(id)
    setCancelReason('')
    setCancelError('')
    setCancelOpen(true)
  }

  const submitCancel = async () => {
    if (!cancelId) return
    setCancelLoading(true)
    setCancelError('')
    try {
      await axios.post(`/api/v1/access_control_exceptions/${cancelId}/cancel`, { cancel_reason: cancelReason || null })
      setCancelOpen(false)
      revalidator.revalidate()
    } catch (err) {
      setCancelError(err?.response?.data?.errors?.join(', ') || 'Error al cancelar')
    } finally {
      setCancelLoading(false)
    }
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <Typography variant="h4">Excepciones de Control de Acceso</Typography>

        {/* Menú de acciones en cascada */}
        <div className="flex gap-2">
          <Menu placement="bottom-end">
            <MenuHandler>
              <Button variant="outlined" className="flex items-center gap-2">
                Acciones
                <FiChevronDown className="h-4 w-4" />
              </Button>
            </MenuHandler>
            <MenuList className="p-1 min-w-[230px]">
              <MenuItem className="p-2">
                <Link to="/controldeacceso" className="flex items-center gap-2 w-full">
                  <FiCornerUpLeft className="h-4 w-4" />
                  <span>Volver a Control de Acceso</span>
                </Link>
              </MenuItem>

              <HasAccess permissions="access#history">
                <MenuItem className="p-2">
                  <Link to="/controldeacceso/exceptions/history" className="flex items-center gap-2 w-full">
                    <FiClock className="h-4 w-4" />
                    <span>Ver historial</span>
                  </Link>
                </MenuItem>
              </HasAccess>

              <HasAccess permissions="access#except">
                <MenuItem className="p-2">
                  <Link to="/controldeacceso/exceptions/new" className="flex items-center gap-2 w-full">
                    <FiPlus className="h-4 w-4" />
                    <span>Agregar excepción</span>
                  </Link>
                </MenuItem>
              </HasAccess>
            </MenuList>
          </Menu>
        </div>
      </div>

      <div className="overflow-auto rounded-lg border border-blue-gray-50">
        <table className="min-w-full text-left">
          <thead className="bg-blue-gray-50">
            <tr>
              <th className="p-3">ID</th>
              <th className="p-3">Sujeto</th>
              <th className="p-3">Dependencia</th>
              <th className="p-3">Tiempo</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Justificación</th>
              <th className="p-3">Otorgada por</th>
              <th className="p-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {records?.length === 0 && (
              <tr><td className="p-3" colSpan={8}>Sin registros</td></tr>
            )}
            {records?.map((raw) => {
              const row = normalizeException(raw)
              const cancelDisabled = row.status === 'canceled'
              return (
                <tr key={row.id} className="border-b last:border-none">
                  <td className="p-3">{row.id}</td>
                  <td className="p-3">
                    <div className="flex flex-col">
                      <span className="font-medium">{row.subject_name || row.subject_type}</span>
                      <span className="text-sm text-blue-gray-600">{row.subject_subtitle}</span>
                    </div>
                  </td>
                  <td className="p-3">{row.office_name || row.office_id || 'Todas'}</td>
                  <td className="p-3">
                    <DateRangeCell start={row.starts_at} end={row.ends_at} />
                  </td>
                  <td className="p-3"><StatusPill value={row.status} /></td>
                  <JustificationCell
                    justification={row.justification}
                    onShowFull={setFullJustification}
                  />
                  <td className="p-3">{row.granted_by_name || row.granted_by_id}</td>
                  <td className="p-3 text-right">
                    <Tooltip content={cancelDisabled ? 'Ya cancelada' : 'Cancelar'}>
                      <span>
                        <IconButton
                          variant="text"
                          onClick={() => openCancel(row.id)}
                          disabled={cancelDisabled}
                        >
                          <FiX className="h-5 w-5" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <Pagination meta={meta} searchParams={searchParams} setSearchParams={setSearchParams} />

      <Dialog open={!!fullJustification} handler={closeJustificationModal} size="md">
        <DialogHeader>Justificación completa</DialogHeader>
        <DialogBody divider className="max-h-[65vh] overflow-auto">
          <Typography className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm leading-6 text-blue-gray-800">
            {fullJustification}
          </Typography>
        </DialogBody>
        <DialogFooter>
          <Button variant="outlined" onClick={closeJustificationModal}>
            Cerrar
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog open={cancelOpen} handler={() => setCancelOpen(false)} size="sm">
        <DialogHeader>Cancelar excepción</DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-3">
            <Textarea
              label="Motivo de cancelación (opcional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            {cancelError && (
              <Typography className="text-sm text-red-600">
                {cancelError}
              </Typography>
            )}
          </div>
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button variant="outlined" onClick={() => setCancelOpen(false)} disabled={cancelLoading}>
            Cerrar
          </Button>
          <Button onClick={submitCancel} disabled={cancelLoading}>
            {cancelLoading ? 'Cancelando…' : 'Confirmar cancelación'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  )
}
